import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ClientCommand, ServerEvent } from '@leadgab/shared';
import { useAuth } from './auth.tsx';
import { refreshSessions } from './sessionCache.ts';
import { useSocket } from './useSocket.ts';
import type { ConnectionStatus } from './useSocket.ts';

/**
 * One socket for the whole signed-in app.
 *
 * It used to be opened by the Chats screen, which meant an agent on Settings
 * or Inventory had no connection at all: their sidebar counts went stale, the
 * rail's badge had to be polled on a timer, and nothing could tell them a
 * customer had written. Anything that wants to react to a message has to be
 * able to hear one from wherever the agent happens to be standing.
 *
 * Mounted by AppLayout, so it covers every authenticated screen and nothing
 * else — the customer's own conversation page and the unsubscribe page live
 * outside it and open their own connections, as they should: those are a
 * different audience with a different credential.
 */

interface RealtimeValue {
  status: ConnectionStatus;
  send: (command: ClientCommand) => boolean;
  /** Registers a listener; returns the function that removes it. */
  subscribe: (listener: (event: ServerEvent) => void) => () => void;
}

const RealtimeContext = createContext<RealtimeValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { me } = useAuth();
  const queryClient = useQueryClient();

  // A Set rather than a single handler: the rail watches for anything worth a
  // badge, the Chats screen patches its own caches, and neither should have to
  // know the other exists.
  const listeners = useRef(new Set<(event: ServerEvent) => void>());

  const subscribe = useCallback((listener: (event: ServerEvent) => void) => {
    listeners.current.add(listener);

    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const handleEvent = useCallback(
    (event: ServerEvent) => {
      // The cache work is done once, here, rather than in every subscriber.
      // Screens that are not Chats still need their lists correct for when the
      // agent walks back to them, and doing it per listener would multiply the
      // refetch by however many happened to be mounted.
      if (
        event.type === 'message' ||
        event.type === 'sessions-changed' ||
        event.type === 'session-assigned'
      ) {
        refreshSessions(queryClient);
      }

      for (const listener of listeners.current) listener(event);
    },
    [queryClient],
  );

  const { status, send } = useSocket({
    chatUuid: me?.chat.uuid ?? null,
    onEvent: handleEvent,
  });

  /**
   * Tells the server whether anybody is actually at the screen.
   *
   * Since the socket stays open on every screen, its existence stopped being
   * evidence of a person — and the server reads that to decide whether a
   * customer's message reached a human, and so whether to email the team.
   * Without this an agent who wandered off with the app open would silently
   * hold back everyone else's notification.
   *
   * Sent on every change and once on connect, because a socket that dropped
   * and came back has a server-side default to correct.
   */
  useEffect(() => {
    if (status !== 'open') return;

    const report = (): void => {
      send({
        type: 'attention',
        watching: document.visibilityState === 'visible' && document.hasFocus(),
      });
    };

    report();

    window.addEventListener('focus', report);
    window.addEventListener('blur', report);
    document.addEventListener('visibilitychange', report);

    return () => {
      window.removeEventListener('focus', report);
      window.removeEventListener('blur', report);
      document.removeEventListener('visibilitychange', report);
    };
  }, [status, send]);

  const value = useMemo<RealtimeValue>(
    () => ({ status, send, subscribe }),
    [status, send, subscribe],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeValue {
  const value = useContext(RealtimeContext);

  if (!value) throw new Error('useRealtime must be used inside RealtimeProvider');

  return value;
}

/**
 * Subscribes to socket events for the life of a component.
 *
 * Takes the handler in a ref so a caller that rebuilds its closure on every
 * render — which is most of them — does not churn the subscription.
 */
export function useRealtimeEvent(handler: (event: ServerEvent) => void): void {
  const { subscribe } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(
    () =>
      subscribe((event) => {
        ref.current(event);
      }),
    [subscribe],
  );
}
